import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './HRJobsPage.css';

// Detects if string contains HTML tags; if not, converts plain text to basic HTML
const toHtml = (text) => {
  if (!text) return '';
  if (/<[a-z][\s\S]*>/i.test(text)) return text; // already HTML
  // Plain text: convert newlines to <br>, wrap in <p>
  return '<p>' + text.replace(/\n{2,}/g, '</p><p>').replace(/\n/g, '<br/>') + '</p>';
};

// Strip all tags to get plain text for truncation
const stripTags = (html) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const TruncatedHtml = ({ html, maxChars = 180 }) => {
  const plain = stripTags(toHtml(html));
  if (plain.length <= maxChars) {
    return <div className="job-description job-description-html" dangerouslySetInnerHTML={{ __html: toHtml(html) }} />;
  }
  return (
    <p className="job-description job-description-truncated">
      {plain.slice(0, maxChars).trimEnd()}…
    </p>
  );
};

const HRJobsPage = () => {
  const { currentUser } = useContext(AuthContext);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [viewingJob, setViewingJob] = useState(null);
  const [applying, setApplying] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [myApplicationJobIds, setMyApplicationJobIds] = useState(new Set());
  const [applicationData, setApplicationData] = useState({
    fullName: '',
    email: '',
    phone: '',
    coverLetter: '',
    resumeFile: null
  });
  const [resumeUpload, setResumeUpload] = useState(null);
  const [dynamicAnswers, setDynamicAnswers] = useState({});
  const [modalShake, setModalShake] = useState(false);

  const handleBackdropClick = () => {
    setModalShake(true);
    setTimeout(() => setModalShake(false), 500);
  };

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const [jobsRes, appsRes] = await Promise.all([
          api.get('/api/hr/jobs/published'),
          api.get('/api/hr/applications?userOnly=true').catch(() => ({ data: { applications: [] } }))
        ]);
        setJobs(jobsRes.data.jobs || []);
        const appliedIds = new Set(
          (appsRes.data.applications || []).map(a => String(a.jobId))
        );
        setMyApplicationJobIds(appliedIds);
      } catch (err) {
        console.error('Error fetching jobs:', err);
        setError('Failed to load job postings');
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  useEffect(() => {
    if (currentUser) {
      const fullName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();
      setApplicationData((prev) => ({
        ...prev,
        fullName: fullName || prev.fullName,
        email: currentUser.email || prev.email
      }));
    }
  }, [currentUser]);

  const featuredJobs = useMemo(() => jobs.filter(job => job.isFeatured), [jobs]);
  const regularJobs = useMemo(() => jobs.filter(job => !job.isFeatured), [jobs]);

  const openApplyModal = (job) => {
    setViewingJob(null);
    setSelectedJob(job);
    setResumeUpload(null);
    setSubmitError('');
    setApplicationData((prev) => ({
      ...prev,
      coverLetter: '',
      resumeFile: null
    }));
    // Init dynamic answers from job sections
    const initAnswers = {};
    (job.sections || []).forEach((section, sIdx) => {
      (section.questions || []).forEach((q, qIdx) => {
        const key = `${sIdx}-${qIdx}`;
        if (q.type === 'multi-select') initAnswers[key] = [];
        else if (q.type === 'checkbox') initAnswers[key] = false;
        else initAnswers[key] = '';
      });
    });
    setDynamicAnswers(initAnswers);
  };

  const closeApplyModal = () => {
    setSelectedJob(null);
    setApplying(false);
    setDynamicAnswers({});
  };

  const openViewModal = (job) => setViewingJob(job);
  const closeViewModal = () => setViewingJob(null);

  const handleDynamicAnswer = (key, value) => {
    setDynamicAnswers(prev => ({ ...prev, [key]: value }));
  };

  const handleMultiSelectToggle = (key, option) => {
    setDynamicAnswers(prev => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      return {
        ...prev,
        [key]: current.includes(option)
          ? current.filter(o => o !== option)
          : [...current, option]
      };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setApplicationData((prev) => ({ ...prev, [name]: value }));
  };

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0] || null;
    setApplicationData((prev) => ({ ...prev, resumeFile: file }));
  };

  const uploadResume = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    if (!selectedJob) return;

    try {
      setApplying(true);
      setSubmitError('');

      let resumePayload = {};
      if (applicationData.resumeFile) {
        const uploadResponse = await uploadResume(applicationData.resumeFile);
        resumePayload = {
          resumeUrl: uploadResponse.url,
          resumeFilename: uploadResponse.filename,
          resumeFileType: uploadResponse.fileType,
          resumeSize: uploadResponse.size
        };
        setResumeUpload(uploadResponse);
      }

      await api.post('/api/hr/applications', {
        applicantName: applicationData.fullName,
        email: applicationData.email,
        phone: applicationData.phone,
        coverLetter: applicationData.coverLetter,
        jobId: selectedJob._id,
        jobTitle: selectedJob.title,
        answers: dynamicAnswers,
        ...resumePayload
      });

      // Mark this job as applied locally so button updates immediately
      setMyApplicationJobIds(prev => new Set([...prev, String(selectedJob._id)]));
      alert('Application submitted successfully!');
      closeApplyModal();
    } catch (err) {
      console.error('Error submitting application:', err);
      setSubmitError(err.response?.data?.message || 'Failed to submit application. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="hr-jobs-page">
        <div className="loading">Loading job postings...</div>
      </div>
    );
  }

  return (
    <div className="hr-jobs-page">
      <div className="page-header">
        <h1>👥 Human Resources</h1>
        <p>Browse open positions and apply online.</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {featuredJobs.length > 0 && (
        <section className="jobs-section featured">
          <h2>⭐ Featured Jobs</h2>
          <div className="jobs-grid">
            {featuredJobs.map(job => (
              <div key={job._id} className="job-card featured">
                <div className="job-card-header">
                  <h3>{job.title}</h3>
                  <span className="badge">Featured</span>
                </div>
                <p className="job-meta">{job.department || 'Department'} • {job.location || 'Location'}</p>
                <div className="job-description job-description-html" dangerouslySetInnerHTML={{ __html: toHtml(job.description) }} />
                <div className="job-card-actions">
                  <button className="btn-secondary" onClick={() => openViewModal(job)}>View More</button>
                  {myApplicationJobIds.has(String(job._id))
                    ? <span className="badge applied-badge">✓ Applied</span>
                    : <button className="btn-primary" onClick={() => openApplyModal(job)}>Apply Now</button>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="jobs-section">
        <h2>Open Positions</h2>
        {regularJobs.length === 0 ? (
          <p className="empty-state">No open positions available at the moment.</p>
        ) : (
          <div className="jobs-grid">
            {regularJobs.map(job => (
              <div key={job._id} className="job-card">
                <h3>{job.title}</h3>
                <p className="job-meta">{job.department || 'Department'} • {job.location || 'Location'}</p>
                <div className="job-details">
                  {job.employmentType && <span>⏱ {job.employmentType}</span>}
                  {job.salaryRange && <span>💰 {job.salaryRange}</span>}
                  {job.applicationDeadline && (
                    <span>📅 Apply by {new Date(job.applicationDeadline).toLocaleDateString()}</span>
                  )}
                </div>
                <div className="job-description job-description-html" dangerouslySetInnerHTML={{ __html: toHtml(job.description) }} />
                <div className="job-card-actions">
                  <button className="btn-secondary" onClick={() => openViewModal(job)}>View More</button>
                  {myApplicationJobIds.has(String(job._id))
                    ? <span className="badge applied-badge">✓ Applied</span>
                    : <button className="btn-primary" onClick={() => openApplyModal(job)}>Apply Now</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* View More Modal */}
      {viewingJob && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content job-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>{viewingJob.title}</h3>
                <p className="job-meta" style={{ marginTop: '0.25rem' }}>{viewingJob.department} • {viewingJob.location}</p>
              </div>
              <button className="modal-close" onClick={closeViewModal}>×</button>
            </div>
            <div className="modal-body job-detail-body">
              <div className="job-detail-meta">
                {viewingJob.employmentType && <span>⏱ {viewingJob.employmentType}</span>}
                {viewingJob.salaryRange && <span>💰 {viewingJob.salaryRange}</span>}
                {viewingJob.applicationDeadline && (
                  <span>📅 Apply by {new Date(viewingJob.applicationDeadline).toLocaleDateString()}</span>
                )}
              </div>
              {viewingJob.description && (
                <div className="job-detail-section">
                  <div className="job-detail-html" dangerouslySetInnerHTML={{ __html: toHtml(viewingJob.description) }} />
                </div>
              )}
              {viewingJob.responsibilities && (
                <div className="job-detail-section">
                  <h4>Responsibilities</h4>
                  <div className="job-detail-html" dangerouslySetInnerHTML={{ __html: toHtml(viewingJob.responsibilities) }} />
                </div>
              )}
              {viewingJob.requirements && (
                <div className="job-detail-section">
                  <h4>Requirements</h4>
                  <div className="job-detail-html" dangerouslySetInnerHTML={{ __html: toHtml(viewingJob.requirements) }} />
                </div>
              )}
              <div className="job-detail-actions">
                {myApplicationJobIds.has(String(viewingJob._id))
                  ? <span className="badge applied-badge">✓ Already Applied</span>
                  : <button className="btn-primary" onClick={() => openApplyModal(viewingJob)}>Apply Now</button>}
                <button className="btn-secondary" onClick={closeViewModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedJob && (
        <div className="modal-overlay" onClick={handleBackdropClick}>
          <div className={`modal-content${modalShake ? ' modal-shake' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Apply for {selectedJob.title}</h3>
              <button className="modal-close" onClick={closeApplyModal}>×</button>
            </div>
            <form className="modal-body" onSubmit={handleSubmitApplication}>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  value={applicationData.fullName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={applicationData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={applicationData.phone}
                  onChange={handleInputChange}
                />
              </div>
              {submitError && (
                <div className="submit-error">{submitError}</div>
              )}
              <div className="form-group">
                <label>Cover Letter</label>
                <textarea
                  name="coverLetter"
                  rows="5"
                  value={applicationData.coverLetter}
                  onChange={handleInputChange}
                />
              </div>

              {/* Dynamic application form sections */}
              {selectedJob.sections && selectedJob.sections.length > 0 && (
                <div className="dynamic-form-sections">
                  {selectedJob.sections.map((section, sIdx) => (
                    <div key={sIdx} className="dynamic-section">
                      <h4 className="dynamic-section-title">{section.label}</h4>
                      {section.description && (
                        <p className="dynamic-section-desc">{section.description}</p>
                      )}
                      {section.questions.map((q, qIdx) => {
                        const key = `${sIdx}-${qIdx}`;
                        return (
                          <div key={qIdx} className="form-group">
                            {q.isHtml ? (
                              <div
                                className="html-question-label"
                                dangerouslySetInnerHTML={{ __html: q.label }}
                              />
                            ) : (
                              <label>
                                {q.label}
                                {q.required && <span className="required-star"> *</span>}
                              </label>
                            )}
                            {q.helpText && <small className="help-text">{q.helpText}</small>}

                            {(q.type === 'text' || q.type === 'email') && (
                              <input
                                type={q.type}
                                value={dynamicAnswers[key] || ''}
                                onChange={(e) => handleDynamicAnswer(key, e.target.value)}
                                placeholder={q.placeholder || ''}
                                required={q.required}
                              />
                            )}
                            {q.type === 'textarea' && (
                              <textarea
                                rows="4"
                                value={dynamicAnswers[key] || ''}
                                onChange={(e) => handleDynamicAnswer(key, e.target.value)}
                                placeholder={q.placeholder || ''}
                                required={q.required}
                              />
                            )}
                            {q.type === 'date' && (
                              <input
                                type="date"
                                value={dynamicAnswers[key] || ''}
                                onChange={(e) => handleDynamicAnswer(key, e.target.value)}
                                required={q.required}
                              />
                            )}
                            {q.type === 'radio' && (
                              <div className="radio-group">
                                {q.options.map((opt) => (
                                  <label key={opt} className="radio-label">
                                    <input
                                      type="radio"
                                      name={`dynamic-${key}`}
                                      value={opt}
                                      checked={dynamicAnswers[key] === opt}
                                      onChange={() => handleDynamicAnswer(key, opt)}
                                      required={q.required}
                                    />
                                    {opt}
                                  </label>
                                ))}
                              </div>
                            )}
                            {q.type === 'checkbox' && (
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={!!dynamicAnswers[key]}
                                  onChange={(e) => handleDynamicAnswer(key, e.target.checked)}
                                  required={q.required && !dynamicAnswers[key]}
                                />
                                {q.isHtml ? (
                                  <span>I confirm the above{q.required && <span className="required-star"> *</span>}</span>
                                ) : 'I agree'}
                              </label>
                            )}
                            {q.type === 'dropdown' && (
                              <select
                                value={dynamicAnswers[key] || ''}
                                onChange={(e) => handleDynamicAnswer(key, e.target.value)}
                                required={q.required}
                              >
                                <option value="">-- Select --</option>
                                {q.options.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            )}
                            {q.type === 'multi-select' && (
                              <div className="checkbox-group">
                                {q.options.map((opt) => (
                                  <label key={opt} className="checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={(dynamicAnswers[key] || []).includes(opt)}
                                      onChange={() => handleMultiSelectToggle(key, opt)}
                                    />
                                    {opt}
                                  </label>
                                ))}
                              </div>
                            )}
                            {q.type === 'file' && (
                              <input
                                type="file"
                                onChange={(e) => handleDynamicAnswer(key, e.target.files?.[0]?.name || '')}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
              <div className="form-group">
                <label>Resume (PDF/DOC/DOCX)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleResumeChange}
                />
                {resumeUpload && (
                  <p className="upload-status">Uploaded: {resumeUpload.filename}</p>
                )}
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={closeApplyModal}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={applying}>
                  {applying ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRJobsPage;
